import { useEffect, useRef, useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FaxHelper } from "@/Common/Helpers/FaxHelper";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { PortfolioHelper } from "../Helpers/PortfolioHelper";
import { MySentFaxTableItem } from "../Models/MyNotesInboxTableItem";

interface IPortfolioFaxInboxStatusFieldProps {
    faxInboxItem: MySentFaxTableItem;
}

function PortfolioFaxInboxStatusField(props: IPortfolioFaxInboxStatusFieldProps): JSX.Element {
    const hoursInterval: number = 3600000;
    const minutesInterval: number = 60000;
    const secondsInterval: number = 1000;

    const { getText } = useTranslation();

    const [currentDateTime, setCurrentDateTime] = useState(new Date());
    const [refreshRetriesTimerInterval, setRefreshRetriesTimerInterval] = useState(secondsInterval);
    const refreshRetriesTimerIntervalId = useRef(null);

    const getSentFaxStatus = (item: MySentFaxTableItem): string => {
        if (FaxHelper.isFailedFax(item.Model.SentStatus)) {
            let statusValue = item.Model.Status;
            if (item.Model?.ProviderName?.toLowerCase() == "documo" && !IsNullOrUndefined(item.Model?.ProviderError))
                statusValue = `${statusValue} - ${PortfolioHelper.getDocumoFaxResultText(item.Model.ProviderError)}`;

            return statusValue;
        }
        return item.Model.Status;
    };

    const getSentFaxtRetryStatus = (item: MySentFaxTableItem): string => {
        if (isCountdownRequired(item)) {
            const luxonCurrentDateTime = LuxonDateTimeParser.parse(currentDateTime);
            const dateDiff = LuxonDateTimeParser.parse(item.Model.NextRetryDate).diff(luxonCurrentDateTime, ["hours", "minutes", "seconds"]);
            const dateDiffMinutes = LuxonDateTimeParser.parse(item.Model.NextRetryDate).diff(luxonCurrentDateTime, ["minutes"]);
            return getText("RetryingInLabel") +
                `${dateDiff.hours ? " " + dateDiff.hours.toString() + getText("HoursAbbrevLabel") : ""}` +
                `${dateDiff.minutes ? " " + dateDiff.minutes.toString() + getText("MinutesAbbreviationLabel") : ""}` +
                `${(dateDiffMinutes.minutes < 1 && dateDiff.seconds) ? " " + Math.round(dateDiff.seconds).toString() + getText("SecondsAbbreviationLabel") : ""}` + " - ";
        }
        return "";
    };

    const isCountdownRequired = (item: MySentFaxTableItem): boolean => FaxHelper.isFailedFax(item.Model.SentStatus) &&
        item.Model.NextRetryDate &&
        item.Model.NextRetryDate > new Date();

    useEffect(() => {
        if (!isCountdownRequired(props.faxInboxItem))
            return;

        refreshRetriesTimerIntervalId.current = window.setInterval(() =>
            setCurrentDateTime(new Date()), refreshRetriesTimerInterval);

        return () => {
            if (refreshRetriesTimerIntervalId.current)
                window.clearInterval(refreshRetriesTimerIntervalId.current);
        };
    }, [props.faxInboxItem, refreshRetriesTimerInterval]);

    useEffect(() => {
        if (!props.faxInboxItem?.Model?.NextRetryDate || !currentDateTime)
            return;

        const luxonCurrentDateTime = LuxonDateTimeParser.parse(currentDateTime);
        const dateDiffMilliseconds = LuxonDateTimeParser.parse(props.faxInboxItem.Model.NextRetryDate).diff(luxonCurrentDateTime, ["milliseconds"]);
        let newInterval = secondsInterval;
        if (dateDiffMilliseconds.milliseconds > hoursInterval * 2)
            newInterval = hoursInterval;
        else if (dateDiffMilliseconds.milliseconds > minutesInterval * 2)
            newInterval = minutesInterval;

        if (newInterval !== refreshRetriesTimerInterval) {
            if (refreshRetriesTimerIntervalId.current)
                window.clearInterval(refreshRetriesTimerIntervalId.current);

            refreshRetriesTimerIntervalId.current = window.setInterval(() =>
                setCurrentDateTime(new Date()), newInterval);

            setRefreshRetriesTimerInterval(newInterval);
        }
    }, [currentDateTime, props.faxInboxItem.Model.NextRetryDate, refreshRetriesTimerInterval]);

    return (
        <div>
            {cacheStore.ClinicSettings.EnableFaxRetries && <span className="text-italic">{getSentFaxtRetryStatus(props.faxInboxItem)}</span>}
            {getSentFaxStatus(props.faxInboxItem)}
        </div>
    );
}

export { PortfolioFaxInboxStatusField };
